import styles from './table.module.css'
import { Button } from "../button/button"
import { define, toDate } from '../../../../utils/typeUtils'
import { useEffect, useRef, useState } from 'react'
import { Action, Column, Data, FieldType, FieldValue } from '../types'
import { StaticValue } from '../static-value/staticValue'
import { ActionButton } from '../action-button/actionButton'
import { Spinner } from '../spinner/spinner'
import { Link } from '../link/link'
import { Pagination } from '../pagination/pagination2'
import { SpinnerDiv } from '../spinner/spinnerDiv'
import { Async } from '../async-components/async/async'
import { O } from '../../state/types'

type Props = {
    columns: Column[]
    rows: Data[]
    rowId: string
    readOnly?: boolean
    disableCreate?: boolean
    emptyMessage?: string
    small?: boolean
    createLabel?: string
    createIcon?: string
    focusCreate?: boolean
    alignRight?: boolean
    className?: string
    currentPage: number
    showNext?: boolean
    loading?: boolean
    onCreate?: () => void
    onEdit?: (rowIdValue: string) => void
    onRemove?: (rowIdValue: string) => void
    actions?: (rowIdValue: string) => Promise<Action[]>
    renderActions?: (rowIdValue: string) => JSX.Element
    renderHeaderActions?: () => JSX.Element | JSX.Element[]
    renderDetail?: (row: Data) => JSX.Element
    onGoToPreviousPage: () => void
    onGoToNextPage: () => void
}

export const Table2 = (props: Props) => {
    const { columns, rows, rowId, readOnly, disableCreate, emptyMessage, small, createLabel, createIcon, focusCreate, currentPage, showNext, loading, onCreate, onEdit, onRemove, actions, renderActions, renderHeaderActions, renderDetail, className, alignRight = true, onGoToPreviousPage, onGoToNextPage } = props
    
    const [openedRow, setOpenedRow] = useState<string>()
    const [height, setHeight] = useState<number>(0)
    const ref = useRef<HTMLDivElement>(null)

    useEffect(() => {
        ref.current && setHeight(ref.current.offsetHeight)
    }, [rows, loading])
    
    const showActionsColumn = !readOnly || actions || renderActions || renderDetail
    const columnsAmount = columns.length + (showActionsColumn ? 1 : 0)

    const defaultRender = (column: Column, rawValue?: FieldValue) => {
        const value = column.type === FieldType.DATE ? toDate(rawValue as O<string>) : rawValue
        return <StaticValue type={column.type} table={column.table} value={value} />
    }

    const onActionsClick = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => e.stopPropagation()

    const renderActionBtn = (rowIdValue: string, actionsLength: number) => (action: Action, index: number) => {
        const onClick = action.onClick ? () => action.onClick!(rowIdValue) : undefined
        const actionClassName = actions && index < actionsLength - 1 ? styles.actionBtn : ''
        return <ActionButton
            key={index}
            action={action}
            className={actionClassName}
            small={action.small}
            onClick={onClick}
        />
    }

    const renderEditBtn = (rowIdValue: string) => {
        const onEditRow = () => onEdit && onEdit(rowIdValue)
        return <Button
            icon="pencil-fill"
            tooltip='Editar'
            small={small}
            rightMargin
            focusable={false}
            onClick={onEditRow}
        />
    }
    
    const renderRemoveBtn = (rowIdValue: string) => {
        const onRemoveRow = () => onRemove && onRemove(rowIdValue)
        return <Button
            kind='secondary'
            icon='trash-fill'
            tooltip='Eliminar'
            small={small}
            focusable={false}
            onClick={onRemoveRow}
        />
    }

    const toggleDetail = (rowIdValue: string) => () => {
        renderDetail && setOpenedRow(prevValue => prevValue === rowIdValue ? undefined : rowIdValue)
    }

    const renderAsync = (element: JSX.Element) => loading ? <SpinnerDiv height={Math.max(height, 500)} /> : element

    return renderAsync(
        <div ref={ref}>
            {rows.length > 0 && (currentPage !== 1 || showNext) && (
                <Pagination
                    currentPage={currentPage}
                    showNext={showNext}
                    onGoToPreviousPage={onGoToPreviousPage}
                    onGoToNextPage={onGoToNextPage}
                />
            )}
            <table className={`table ${openedRow ? '' : 'table-hover'} ${className || ''}`}>
                <thead>
                    <tr className={alignRight ? styles.alignRight : ''}>
                        {columns.map(colum => (
                            <th key={colum.name} scope="col" className={styles.th}>
                                {rows.length > 0 ? (
                                    colum.renderLabel ? colum.renderLabel() : colum.label
                                ) : ''}
                            </th>
                        ))}
                        {showActionsColumn && (
                            <th scope="col">
                                <div className={styles.actionsContainer}>
                                    {renderHeaderActions ? renderHeaderActions() :
                                        !readOnly && onCreate && (
                                            <Button
                                                label={createLabel || 'Agregar'}
                                                icon={createIcon}
                                                small={small}
                                                disabled={disableCreate}
                                                focus={focusCreate}
                                                onClick={onCreate}
                                                className={styles.createBtn}
                                            />
                                        )
                                    }
                                </div>
                            </th>
                        )}
                    </tr>
                </thead>
                {rows.map(row => {
                    const rowIdValue = define(row[rowId]).toString() as string
                    const showDetail = openedRow === rowIdValue
                    const detail = renderDetail && renderDetail(row)
                    const expandedClass = showDetail ? styles.expanded : ''
                    
                    return (
                        <tbody key={rowIdValue}>
                            <tr onClick={toggleDetail(rowIdValue)} className={alignRight ? styles.alignRight : ''}>
                                {columns.map(column => {
                                    const value = row[column.name]
                                    const tdClass = `${styles.td} ${column.type === FieldType.IMAGE ? styles.imgTd : ''}`
                                    return (
                                        <td key={column.name} className={tdClass}>
                                            {column.loading ? <Spinner centered={false} /> : (
                                                column.render ? column.render(value) : defaultRender(column, value)
                                            )}
                                        </td>
                                    )
                                })}
                                {(showActionsColumn || !!detail) && (
                                    <td className={`${styles.td} ${styles.actionsTd}`}>
                                        <div className={styles.actionsContainer} onClick={onActionsClick}>
                                            
                                            <Async
                                                loadFn={async () => {
                                                    const actionList = actions && await actions(rowIdValue)
                                                    return (
                                                        <>
                                                            {actionList?.map(renderActionBtn(rowIdValue, actionList.length)) || (
                                                                !readOnly && (
                                                                    <>
                                                                        {onEdit && renderEditBtn(rowIdValue)}
                                                                        {onRemove && renderRemoveBtn(rowIdValue)}
                                                                    </>
                                                                )
                                                            )}
                                                        </>
                                                    )
                                                }}
                                                deps={[]}
                                            />
                                            
                                            {renderActions && renderActions(rowIdValue)}
                                            {detail && <i
                                                onClick={toggleDetail(rowIdValue)}
                                                className={`bi bi-chevron-down ${styles.expandIcon} ${expandedClass}`}
                                            />}
                                        </div>
                                    </td>
                                )}
                            </tr>
                            {detail && showDetail && (
                                <tr className={styles.detailTr}>
                                    <td colSpan={columnsAmount}>
                                        {detail}
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    )
                })}
            </table>
            {rows.length > 0 ? (
                <Pagination
                    currentPage={currentPage}
                    showNext={showNext}
                    onGoToPreviousPage={onGoToPreviousPage}
                    onGoToNextPage={onGoToNextPage}
                />
            ) : (
                <div className={styles.empty}>
                    <i className={`bi bi-bricks`} />
                    <span>{emptyMessage || 'Esta tabla está vacía.'}</span>
                    {!readOnly && onCreate && (
                        <Link
                            text='Crear el primer item'
                            kind="primary"
                            onClick={onCreate}
                            className={styles.emptyLink}
                        />
                    )}
                </div>
            )}
        </div>
    )
}
